import { Component } from '@theme/component';

/**
 * @typedef {Object} SubcollectionSliderRefs
 * @property {HTMLElement} track - The scrollable track containing collection items
 * @property {HTMLElement} trackContainer - The overflow-hidden container wrapping the track
 * @property {HTMLButtonElement} prevButton - The previous arrow button
 * @property {HTMLButtonElement} nextButton - The next arrow button
 */

/**
 * A custom element that manages a horizontally scrollable sub-collection pill slider.
 * Automatically hides arrows and applies alignment when content fits without scrolling.
 *
 * @extends {Component<SubcollectionSliderRefs>}
 */
class SubcollectionSlider extends Component {
    requiredRefs = ['track', 'trackContainer', 'prevButton', 'nextButton'];

    /**
     * Current scroll offset in pixels
     * @type {number}
     */
    #offset = 0;

    /**
     * X position where a touch gesture started
     * @type {number}
     */
    #touchStartX = 0;

    /**
     * ResizeObserver watching the track container for width changes
     * @type {ResizeObserver | undefined}
     */
    #resizeObserver;

    connectedCallback() {
        super.connectedCallback();

        this.#resizeObserver = new ResizeObserver(this.#onResize);
        this.#resizeObserver.observe(this.refs.trackContainer);

        this.refs.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.refs.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
    }

    disconnectedCallback() {
        this.#resizeObserver?.disconnect();

        this.refs.track.removeEventListener('touchstart', this.onTouchStart);
        this.refs.track.removeEventListener('touchend', this.onTouchEnd);
    }

    /**
     * Debounces resize events through requestAnimationFrame
     */
    #onResize = () => requestAnimationFrame(() => this.#applyMode());

    /**
     * Captures the X position of a touch gesture start
     * @param {Event} e
     */
    onTouchStart = (e) => {
        this.#touchStartX = /** @type {TouchEvent} */ (e).touches[0].clientX;
    };

    /**
     * Determines swipe direction and slides accordingly
     * @param {Event} e
     */
    onTouchEnd = (e) => {
        const diff = this.#touchStartX - /** @type {TouchEvent} */ (e).changedTouches[0].clientX;
        if (Math.abs(diff) > 40) this.#slide(diff > 0 ? 1 : -1);
    };

    /**
     * Slides the track to the previous set of items
     */
    onPrev = () => this.#slide(-1);

    /**
     * Slides the track to the next set of items
     */
    onNext = () => this.#slide(1);

    /**
     * Returns true if the track content overflows the container
     * @returns {boolean}
     */
    #needsSlider() {
        const { track, trackContainer } = this.refs;
        return track.scrollWidth > trackContainer.clientWidth + 1;
    }

    /**
     * Switches between slider mode and static aligned mode
     * depending on whether content overflows the container
     */
    #applyMode() {
        const { track, trackContainer, prevButton, nextButton } = this.refs;
        const isStatic = !this.#needsSlider();

        trackContainer.classList.toggle('is-static', isStatic);
        prevButton.classList.toggle('is-hidden', isStatic);
        nextButton.classList.toggle('is-hidden', isStatic);

        if (isStatic) {
            this.#offset = 0;
            track.style.transform = 'translateX(0)';
        } else {
            this.#updateButtons();
        }

        if (!this.classList.contains('is-ready')) {
            this.classList.add('is-ready');
        }
    }

    /**
     * Updates the disabled state of prev/next buttons
     * based on the current scroll offset
     */
    #updateButtons() {
        const { track, trackContainer, prevButton, nextButton } = this.refs;
        const max = track.scrollWidth - trackContainer.clientWidth;
        prevButton.disabled = this.#offset <= 0;
        nextButton.disabled = this.#offset >= max;
    }

    /**
     * Scrolls the track by a percentage of the container width
     * @param {number} direction - 1 for next, -1 for previous
     */
    #slide(direction) {
        const { track, trackContainer } = this.refs;
        const max = track.scrollWidth - trackContainer.clientWidth;
        const step = trackContainer.clientWidth * 0.7;
        this.#offset = Math.max(0, Math.min(this.#offset + direction * step, max));
        track.style.transform = `translateX(-${this.#offset}px)`;
        this.#updateButtons();
    }
}

if (!customElements.get('subcollection-slider')) {
    customElements.define('subcollection-slider', SubcollectionSlider);
}
